import { supabase } from './supabase';

export const scheduleService = {
  async generateSchedule(seasonId, weekToGenerate = 1) {
    try {
      const { data: allSeasonTeams, error: seasonTeamsError } = await supabase
        .from('team_standings')
        .select('team_id, wins, losses, points_for, points_against')
        .eq('season_id', seasonId);

      if (seasonTeamsError) throw seasonTeamsError;

      const teamIds = allSeasonTeams.map(ts => ts.team_id);
      const numTeams = teamIds.length;

      if (numTeams < 2) {
        throw new Error('At least 2 teams required to generate schedule');
      }

      if (weekToGenerate < 1 || weekToGenerate > numTeams - 1) {
        throw new Error(`Week must be between 1 and ${numTeams - 1}`);
      }

      // Fetch all existing games to check for rematches
      const { data: existingGames, error: gamesError } = await supabase
        .from('games')
        .select('team1_id, team2_id')
        .eq('season_id', seasonId);

      if (gamesError) throw gamesError;

      // Create standings map with performance data
      const standingsMap = {};
      allSeasonTeams.forEach(ts => {
        standingsMap[ts.team_id] = {
          wins: ts.wins,
          losses: ts.losses,
          pointsFor: ts.points_for,
          pointsAgainst: ts.points_against
        };
      });

      const weekGames = this._generateWeekGamesSmarter(
        teamIds,
        weekToGenerate,
        numTeams,
        existingGames || [],
        standingsMap
      );

      const gamesWithSeason = weekGames.map(game => ({
        ...game,
        season_id: seasonId,
      }));

      const { data: insertedGames, error: insertError } = await supabase
        .from('games')
        .insert(gamesWithSeason)
        .select();

      if (insertError) throw insertError;

      return {
        success: true,
        message: `Week ${weekToGenerate} schedule generated with ${insertedGames.length} games`,
        games: insertedGames
      };
    } catch (error) {
      console.error('Error generating schedule:', error);
      throw error;
    }
  },

  _generateWeekGamesSmarter(teamIds, week, numTeams, existingGames, standingsMap) {
    const games = [];
    
    // Create set of existing matchups to avoid rematches
    const matchupSet = new Set();
    existingGames.forEach(game => {
      const key1 = `${Math.min(game.team1_id, game.team2_id)}-${Math.max(game.team1_id, game.team2_id)}`;
      matchupSet.add(key1);
    });

    // Sort teams by win percentage (strength)
    const sortedTeams = [...teamIds].sort((a, b) => {
      const aRecord = standingsMap[a];
      const bRecord = standingsMap[b];
      const aWinPct = aRecord.wins / (aRecord.wins + aRecord.losses || 1);
      const bWinPct = bRecord.wins / (bRecord.wins + bRecord.losses || 1);
      return bWinPct - aWinPct; // Descending order
    });

    // Handle odd number of teams
    const teamsPlaying = numTeams % 2 === 1 
      ? sortedTeams.filter((_, i) => i !== (week - 1) % numTeams)
      : sortedTeams;

    // Divide teams into tiers (top 6, bottom 6)
    const tierSize = 6;
    const tiers = [];
    for (let i = 0; i < teamsPlaying.length; i += tierSize) {
      tiers.push(teamsPlaying.slice(i, i + tierSize));
    }

    // Generate matchups within each tier, rotating to avoid rematches
    for (const tier of tiers) {
      if (tier.length < 2) continue;

      // For each tier, create matchups with rotation to avoid same pairs
      const tierGames = this._generateTierMatchups(tier, week, matchupSet);
      tierGames.forEach(game => {
        matchupSet.add(`${Math.min(game.team1_id, game.team2_id)}-${Math.max(game.team1_id, game.team2_id)}`);
        games.push(game);
      });
    }

    return games;
  },

  _generateTierMatchups(tierTeams, week, matchupSet) {
    const games = [];
    const n = tierTeams.length;
    
    if (n < 2) return games;

    // Odd teams: get a bye
    const hasOddTeam = n % 2 === 1;
    const teamsToMatch = hasOddTeam ? tierTeams.slice(0, -1) : tierTeams;

    // Use round-robin within tier with rotation to avoid rematches when possible
    // Simple approach: pair first with last, second with second-to-last, etc.
    // Then rotate based on week to create different pairings each week
    const rotated = [...teamsToMatch];
    const rotationOffset = (week - 1) % teamsToMatch.length;
    
    for (let i = 0; i < rotationOffset; i++) {
      rotated.push(rotated.shift());
    }

    // Create pairings: 1st with last, 2nd with 2nd-to-last, etc.
    // Priority: avoid rematches, but ensure all teams play
    const usedTeams = new Set();
    const availableTeams = [...rotated];
    
    for (let i = 0; i < rotated.length / 2; i++) {
      if (availableTeams.length < 2) break;
      
      const team1 = availableTeams[0];
      availableTeams.shift();
      
      // Try to find best opponent for team1 (preferring no rematch)
      let bestOpponent = null;
      let bestIsRematch = true;
      
      for (let j = 0; j < availableTeams.length; j++) {
        const potentialTeam = availableTeams[j];
        const matchupKey = `${Math.min(team1, potentialTeam)}-${Math.max(team1, potentialTeam)}`;
        const isRematch = matchupSet.has(matchupKey);
        
        // Prefer teams that haven't played, but take anyone if needed
        if (bestOpponent === null || (!isRematch && bestIsRematch)) {
          bestOpponent = potentialTeam;
          bestIsRematch = isRematch;
        }
      }
      
      if (bestOpponent !== null) {
        const matchupKey = `${Math.min(team1, bestOpponent)}-${Math.max(team1, bestOpponent)}`;
        matchupSet.add(matchupKey);
        availableTeams.splice(availableTeams.indexOf(bestOpponent), 1);
        
        games.push({
          team1_id: team1,
          team2_id: bestOpponent,
          week: week,
          is_completed: false,
        });
      }
    }

    return games;
  },

  async getScheduleBySeason(seasonId) {
    try {
      const { data, error } = await supabase
        .from('games')
        .select(`
          id,
          week,
          scheduled_date,
          is_completed,
          team1_id,
          team2_id,
          highlights_url,
          game_scores (
            team1_score,
            team2_score
          ),
          team1:teams!games_team1_id_fkey(id, name, abbreviation),
          team2:teams!games_team2_id_fkey(id, name, abbreviation)
        `)
        .eq('season_id', seasonId)
        .order('week', { ascending: true })
        .order('id', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching schedule:', error);
      throw error;
    }
  },

  async getGamesByWeek(seasonId, week) {
    try {
      const { data, error } = await supabase
        .from('games')
        .select(`
          id,
          week,
          scheduled_date,
          is_completed,
          team1_id,
          team2_id,
          highlights_url,
          game_scores (
            team1_score,
            team2_score
          ),
          team1:teams!games_team1_id_fkey(id, name, abbreviation),
          team2:teams!games_team2_id_fkey(id, name, abbreviation)
        `)
        .eq('season_id', seasonId)
        .eq('week', week)
        .order('id', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching games by week:', error);
      throw error;
    }
  },

  async updateGameScore(gameId, team1Score, team2Score) {
    try {
      const { data: game, error: gameError } = await supabase
        .from('games')
        .select('id, season_id, team1_id, team2_id')
        .eq('id', gameId)
        .single();

      if (gameError) throw gameError;

      const { data: existingScore, error: checkError } = await supabase
        .from('game_scores')
        .select('team1_score, team2_score')
        .eq('game_id', gameId)
        .single();

      let scoreResult;
      let isUpdate = false;
      let previousScore = null;

      if (existingScore) {
        isUpdate = true;
        previousScore = existingScore;
        const { data, error } = await supabase
          .from('game_scores')
          .update({
            team1_score: team1Score,
            team2_score: team2Score,
            updated_at: new Date().toISOString()
          })
          .eq('game_id', gameId)
          .select();

        if (error) throw error;
        scoreResult = data[0];
      } else {
        const { data, error } = await supabase
          .from('game_scores')
          .insert({
            game_id: gameId,
            team1_score: team1Score,
            team2_score: team2Score
          })
          .select();

        if (error) throw error;
        scoreResult = data[0];
      }

      await this._updateStandings(
        game.season_id,
        game.team1_id,
        game.team2_id,
        team1Score,
        team2Score,
        isUpdate,
        previousScore
      );

      const { data: updatedGame, error: updateGameError } = await supabase
        .from('games')
        .update({
          is_completed: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', gameId)
        .select();

      if (updateGameError) throw updateGameError;

      return { success: true, score: scoreResult };
    } catch (error) {
      console.error('Error updating game score:', error);
      throw error;
    }
  },

  async _updateStandings(seasonId, team1Id, team2Id, team1Score, team2Score, isUpdate = false, previousScore = null) {
    try {
      const { data: team1Standing } = await supabase
        .from('team_standings')
        .select('id, wins, losses, points_for, points_against')
        .eq('season_id', seasonId)
        .eq('team_id', team1Id)
        .single();

      const { data: team2Standing } = await supabase
        .from('team_standings')
        .select('id, wins, losses, points_for, points_against')
        .eq('season_id', seasonId)
        .eq('team_id', team2Id)
        .single();

      let team1Updates = {
        points_for: team1Standing.points_for,
        points_against: team1Standing.points_against,
        wins: team1Standing.wins,
        losses: team1Standing.losses
      };

      let team2Updates = {
        points_for: team2Standing.points_for,
        points_against: team2Standing.points_against,
        wins: team2Standing.wins,
        losses: team2Standing.losses
      };

      if (isUpdate && previousScore) {
        const prevTeam1Score = previousScore.team1_score;
        const prevTeam2Score = previousScore.team2_score;

        team1Updates.points_for -= prevTeam1Score;
        team1Updates.points_against -= prevTeam2Score;
        team2Updates.points_for -= prevTeam2Score;
        team2Updates.points_against -= prevTeam1Score;

        if (prevTeam1Score > prevTeam2Score) {
          team1Updates.wins -= 1;
          team2Updates.losses -= 1;
        } else if (prevTeam2Score > prevTeam1Score) {
          team1Updates.losses -= 1;
          team2Updates.wins -= 1;
        }
      }

      team1Updates.points_for += team1Score;
      team1Updates.points_against += team2Score;
      team2Updates.points_for += team2Score;
      team2Updates.points_against += team1Score;

      if (team1Score > team2Score) {
        team1Updates.wins += 1;
        team2Updates.losses += 1;
      } else if (team2Score > team1Score) {
        team1Updates.losses += 1;
        team2Updates.wins += 1;
      }

      team1Updates.updated_at = new Date().toISOString();
      team2Updates.updated_at = new Date().toISOString();

      await supabase
        .from('team_standings')
        .update(team1Updates)
        .eq('id', team1Standing.id);

      await supabase
        .from('team_standings')
        .update(team2Updates)
        .eq('id', team2Standing.id);

      await this._recalculateRankings(seasonId);
    } catch (error) {
      console.error('Error updating standings:', error);
      throw error;
    }
  },

  async _recalculateRankings(seasonId) {
    try {
      const { data: allStandings, error } = await supabase
        .from('team_standings')
        .select('id, team_id, wins, losses, points_for, points_against')
        .eq('season_id', seasonId);

      if (error) throw error;

      const sortedStandings = allStandings.sort((a, b) => {
        const aWins = a.wins;
        const bWins = b.wins;
        
        if (aWins !== bWins) return bWins - aWins;
        
        const aLosses = a.losses;
        const bLosses = b.losses;
        
        if (aLosses !== bLosses) return aLosses - bLosses;
        
        const aDiff = (a.points_for || 0) - (a.points_against || 0);
        const bDiff = (b.points_for || 0) - (b.points_against || 0);
        
        return bDiff - aDiff;
      });

      const updates = sortedStandings.map((standing, index) => ({
        id: standing.id,
        rank: index + 1
      }));

      for (const update of updates) {
        await supabase
          .from('team_standings')
          .update({ rank: update.rank })
          .eq('id', update.id);
      }
    } catch (error) {
      console.error('Error recalculating rankings:', error);
      throw error;
    }
  },

  async regenerateSmartSchedule(seasonId) {
    try {
      // Delete existing schedule
      await this.deleteSchedule(seasonId);

      // Get team standings
      const { data: allSeasonTeams, error: seasonTeamsError } = await supabase
        .from('team_standings')
        .select('team_id, wins, losses, points_for, points_against')
        .eq('season_id', seasonId);

      if (seasonTeamsError) throw seasonTeamsError;

      const teamIds = allSeasonTeams.map(ts => ts.team_id);
      const numTeams = teamIds.length;

      if (numTeams < 2) {
        throw new Error('At least 2 teams required to generate schedule');
      }

      // Create standings map with performance data
      const standingsMap = {};
      allSeasonTeams.forEach(ts => {
        standingsMap[ts.team_id] = {
          wins: ts.wins,
          losses: ts.losses,
          pointsFor: ts.points_for,
          pointsAgainst: ts.points_against
        };
      });

      // Generate games for all weeks
      const allGames = [];
      const existingGames = [];

      for (let week = 1; week < numTeams; week++) {
        const weekGames = this._generateWeekGamesSmarter(
          teamIds,
          week,
          numTeams,
          existingGames,
          standingsMap
        );
        
        // Add week games to tracking and output
        weekGames.forEach(game => {
          existingGames.push(game);
          allGames.push({
            ...game,
            season_id: seasonId,
          });
        });
      }

      // Insert all games at once
      const { data: insertedGames, error: insertError } = await supabase
        .from('games')
        .insert(allGames)
        .select();

      if (insertError) throw insertError;

      return {
        success: true,
        message: `Complete schedule regenerated with ${insertedGames.length} games over ${numTeams - 1} weeks`,
        games: insertedGames
      };
    } catch (error) {
      console.error('Error regenerating schedule:', error);
      throw error;
    }
  },

  async generateRemainingWeeksFairly(seasonId) {
    try {
      // Get current schedule to determine last week
      const { data: currentGames, error: gamesError } = await supabase
        .from('games')
        .select('team1_id, team2_id, week')
        .eq('season_id', seasonId)
        .order('week', { ascending: false });

      if (gamesError) throw gamesError;

      // Get team standings
      const { data: allSeasonTeams, error: seasonTeamsError } = await supabase
        .from('team_standings')
        .select('team_id, wins, losses, points_for, points_against')
        .eq('season_id', seasonId);

      if (seasonTeamsError) throw seasonTeamsError;

      const teamIds = allSeasonTeams.map(ts => ts.team_id);
      const numTeams = teamIds.length;

      if (numTeams < 2) {
        throw new Error('At least 2 teams required to generate schedule');
      }

      const lastWeek = currentGames.length > 0 
        ? Math.max(...currentGames.map(g => g.week))
        : 0;

      if (lastWeek >= numTeams - 1) {
        throw new Error('Season schedule is already complete');
      }

      // Create standings map with performance data
      const standingsMap = {};
      allSeasonTeams.forEach(ts => {
        standingsMap[ts.team_id] = {
          wins: ts.wins,
          losses: ts.losses,
          pointsFor: ts.points_for,
          pointsAgainst: ts.points_against
        };
      });

      // Generate remaining weeks using fair algorithm
      const allGames = [];
      const existingGames = currentGames || [];

      for (let week = lastWeek + 1; week < numTeams; week++) {
        const weekGames = this._generateWeekGamesSmarter(
          teamIds,
          week,
          numTeams,
          existingGames,
          standingsMap
        );
        
        weekGames.forEach(game => {
          existingGames.push(game);
          allGames.push({
            ...game,
            season_id: seasonId,
          });
        });
      }

      // Insert new games
      const { data: insertedGames, error: insertError } = await supabase
        .from('games')
        .insert(allGames)
        .select();

      if (insertError) throw insertError;

      return {
        success: true,
        message: `Generated fair schedule for ${insertedGames.length} remaining games (weeks ${lastWeek + 1}-${numTeams - 1})`,
        games: insertedGames
      };
    } catch (error) {
      console.error('Error generating remaining weeks:', error);
      throw error;
    }
  },

  async deleteSchedule(seasonId) {
    try {
      const { error } = await supabase
        .from('games')
        .delete()
        .eq('season_id', seasonId);

      if (error) throw error;
      return { success: true, message: 'Schedule deleted' };
    } catch (error) {
      console.error('Error deleting schedule:', error);
      throw error;
    }
  },

  async deleteWeek(seasonId, weekNumber) {
    try {
      const { error } = await supabase
        .from('games')
        .delete()
        .eq('season_id', seasonId)
        .eq('week', weekNumber);

      if (error) throw error;
      return { success: true, message: `Week ${weekNumber} deleted` };
    } catch (error) {
      console.error('Error deleting week:', error);
      throw error;
    }
  },

  async updateGameTeams(gameId, team1Id, team2Id) {
    try {
      const { data, error } = await supabase
        .from('games')
        .update({
          team1_id: team1Id,
          team2_id: team2Id,
          updated_at: new Date().toISOString()
        })
        .eq('id', gameId)
        .select();

      if (error) throw error;
      return { success: true, game: data[0] };
    } catch (error) {
      console.error('Error updating game teams:', error);
      throw error;
    }
  },

  async switchGameTeams(gameId) {
    try {
      const { data: game, error: fetchError } = await supabase
        .from('games')
        .select('team1_id, team2_id')
        .eq('id', gameId)
        .single();

      if (fetchError) throw fetchError;

      const { data, error } = await supabase
        .from('games')
        .update({
          team1_id: game.team2_id,
          team2_id: game.team1_id,
          updated_at: new Date().toISOString()
        })
        .eq('id', gameId)
        .select();

      if (error) throw error;
      return { success: true, game: data[0] };
    } catch (error) {
      console.error('Error switching game teams:', error);
      throw error;
    }
  }
};
